import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CbPartType,
  CbPartConfig,
  CbPart,
  PayloadFunction,
  CB_PART_LABELS,
  defaultCbPart,
  defaultPayloadFunction,
} from '../types/payloadTypes';

interface PayloadFunctionsCardProps {
  placeholderIds: number[];
  payloadFunctions: PayloadFunction[];
  onChange: (fns: PayloadFunction[]) => void;
}

const PayloadFunctionsCard: React.FC<PayloadFunctionsCardProps> = ({
  placeholderIds,
  payloadFunctions,
  onChange,
}) => {
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState<number | null>(
    placeholderIds.length > 0 ? placeholderIds[0] : null,
  );

  const monoSx = { fontFamily: '"Space Mono", monospace', fontSize: '0.78rem' };
  const chipSx = (active: boolean) => ({
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.7rem',
    cursor: 'pointer',
    borderColor: active ? theme.palette.primary.main : alpha(theme.palette.divider, 0.7),
    ...(active && theme.palette.mode === 'dark' && {
      '& .MuiChip-label': { color: '#0a0e17', fontWeight: 700 },
    }),
  });

  // Keep selectedId valid when placeholderIds changes
  React.useEffect(() => {
    if (selectedId !== null && !placeholderIds.includes(selectedId)) {
      setSelectedId(placeholderIds.length > 0 ? placeholderIds[0] : null);
    } else if (selectedId === null && placeholderIds.length > 0) {
      setSelectedId(placeholderIds[0]);
    }
  }, [placeholderIds, selectedId]);

  const getFn = useCallback(
    (id: number): PayloadFunction => {
      return payloadFunctions.find((f) => f.id === id) ?? defaultPayloadFunction(id);
    },
    [payloadFunctions],
  );

  const updateFn = useCallback(
    (id: number, patch: Partial<PayloadFunction>) => {
      const existing = getFn(id);
      const updated = { ...existing, ...patch };
      const next = payloadFunctions.filter((f) => f.id !== id);
      next.push(updated);
      next.sort((a, b) => a.id - b.id);
      onChange(next);
    },
    [getFn, onChange, payloadFunctions],
  );

  const renderPartConfig = (id: number, part: PayloadFunction) => {
    const cfg: CbPartConfig = part.config ?? {};

    const update = (patch: Partial<CbPart>) => {
      updateFn(id, { ...part, ...patch });
    };
    const updateConfig = (patch: Partial<CbPartConfig>) =>
      update({ config: { ...(part.config ?? {}), ...patch } });

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
        {/* Type selector */}
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ fontSize: '0.78rem' }}>Function type</InputLabel>
          <Select
            value={part.type}
            label="Function type"
            onChange={(e) => updateFn(id, { ...defaultCbPart(e.target.value as CbPartType), id })}
            sx={monoSx}
          >
            {(Object.keys(CB_PART_LABELS) as CbPartType[]).map((t) => (
              <MenuItem key={t} value={t} sx={monoSx}>
                {CB_PART_LABELS[t]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Static */}
        {part.type === 'static' && (
          <TextField
            size="small"
            fullWidth
            placeholder="literal replacement value"
            value={part.value ?? ''}
            onChange={(e) => update({ value: e.target.value })}
            sx={{ '& input': monoSx }}
          />
        )}

        {/* Random String */}
        {part.type === 'random_string' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="min length"
                type="number"
                value={cfg.min_l ?? 8}
                onChange={(e) => updateConfig({ min_l: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1 }}
                sx={{ flex: 1, '& input': monoSx }}
              />
              <TextField
                size="small"
                label="max length"
                type="number"
                value={cfg.max_l ?? 14}
                onChange={(e) => updateConfig({ max_l: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1 }}
                sx={{ flex: 1, '& input': monoSx }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(
                [
                  ['a-z', 'lowercase', cfg.lowercase !== false],
                  ['0-9', 'digits', cfg.digits !== false],
                  ['A-Z', 'uppercase', !!cfg.uppercase],
                  ['!@#', 'special_chars', !!cfg.special_chars],
                ] as [string, keyof CbPartConfig, boolean][]
              ).map(([label, key, checked]) => (
                <Chip
                  key={key}
                  label={label}
                  size="small"
                  clickable
                  onClick={() => updateConfig({ [key]: !checked })}
                  color={checked ? 'primary' : 'default'}
                  variant={checked ? 'filled' : 'outlined'}
                  sx={chipSx(checked)}
                />
              ))}
            </Box>
            <TextField
              size="small"
              label="Custom charset (overrides chips)"
              placeholder="e.g. abcABC123 or leave empty"
              value={cfg.custom_chars ?? ''}
              onChange={(e) => updateConfig({ custom_chars: e.target.value })}
              sx={{ '& input': monoSx }}
              helperText={
                cfg.custom_chars
                  ? 'Using custom charset — chip settings ignored'
                  : 'Optional: type exact characters to sample from'
              }
              FormHelperTextProps={{ sx: { fontSize: '0.68rem', mt: 0.25 } }}
            />
          </Box>
        )}

        {/* Random Number */}
        {part.type === 'random_number' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignSelf: 'flex-start',
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                overflow: 'hidden',
              }}
            >
              {(['digit-length', 'value-range'] as const).map((mode, i) => {
                const active = mode === 'value-range' ? !!cfg.range_mode : !cfg.range_mode;
                return (
                  <Button
                    key={mode}
                    disableElevation
                    onClick={() => updateConfig({ range_mode: mode === 'value-range' })}
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      px: 1.75,
                      py: 0.65,
                      minWidth: 0,
                      textTransform: 'none',
                      borderRadius: 0,
                      borderRight: i === 0 ? `1px solid ${alpha(theme.palette.divider, 0.7)}` : 'none',
                      backgroundColor: active ? theme.palette.primary.main : 'transparent',
                      color: active
                        ? theme.palette.mode === 'dark'
                          ? '#0a0e17'
                          : '#ffffff'
                        : theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: active
                          ? theme.palette.primary.dark ?? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    {mode === 'digit-length' ? 'Digit length' : 'Value range'}
                  </Button>
                );
              })}
            </Box>
            {!cfg.range_mode ? (
              <>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    label="min digits"
                    type="number"
                    value={cfg.min_l ?? 8}
                    onChange={(e) => updateConfig({ min_l: Math.max(1, parseInt(e.target.value) || 1) })}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1, '& input': monoSx }}
                  />
                  <TextField
                    size="small"
                    label="max digits"
                    type="number"
                    value={cfg.max_l ?? 14}
                    onChange={(e) => updateConfig({ max_l: Math.max(1, parseInt(e.target.value) || 1) })}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1, '& input': monoSx }}
                  />
                </Box>
                <Chip
                  label="leading zeros"
                  size="small"
                  clickable
                  onClick={() => updateConfig({ allow_leading_zeros: !cfg.allow_leading_zeros })}
                  color={cfg.allow_leading_zeros ? 'primary' : 'default'}
                  variant={cfg.allow_leading_zeros ? 'filled' : 'outlined'}
                  sx={{ ...chipSx(!!cfg.allow_leading_zeros), alignSelf: 'flex-start' }}
                />
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label="min value"
                  type="number"
                  value={cfg.min_value ?? 0}
                  onChange={(e) => updateConfig({ min_value: parseInt(e.target.value) || 0 })}
                  sx={{ flex: 1, '& input': monoSx }}
                />
                <TextField
                  size="small"
                  label="max value"
                  type="number"
                  value={cfg.max_value ?? 99999}
                  onChange={(e) => updateConfig({ max_value: parseInt(e.target.value) || 0 })}
                  sx={{ flex: 1, '& input': monoSx }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Random Base64 */}
        {part.type === 'random_base64' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="min length"
                type="number"
                value={cfg.min_l ?? 8}
                onChange={(e) => updateConfig({ min_l: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1 }}
                sx={{ flex: 1, '& input': monoSx }}
              />
              <TextField
                size="small"
                label="max length"
                type="number"
                value={cfg.max_l ?? 14}
                onChange={(e) => updateConfig({ max_l: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1 }}
                sx={{ flex: 1, '& input': monoSx }}
              />
            </Box>
            <Chip
              label="URL-safe  (-_ instead of +/)"
              size="small"
              clickable
              onClick={() => updateConfig({ url_safe: !cfg.url_safe })}
              color={cfg.url_safe ? 'primary' : 'default'}
              variant={cfg.url_safe ? 'filled' : 'outlined'}
              sx={{ ...chipSx(!!cfg.url_safe), alignSelf: 'flex-start' }}
            />
          </Box>
        )}
      </Box>
    );
  };

  if (placeholderIds.length === 0) return null;

  const selectedFn = selectedId !== null ? getFn(selectedId) : null;

  return (
    <Box
      sx={{
        mt: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.7 : 0.9)}`,
        background: alpha(
          theme.palette.background.paper,
          theme.palette.mode === 'dark' ? 0.55 : 0.92,
        ),
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.12)}, transparent)`,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              Payload Functions
            </Typography>
            <Chip
              label={placeholderIds.length}
              size="small"
              color="primary"
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.65rem',
                height: 18,
                '& .MuiChip-label': { px: 0.75 },
                ...(theme.palette.mode === 'dark' && {
                  '& .MuiChip-label': { color: '#0a0e17', fontWeight: 700, px: 0.75 },
                }),
              }}
            />
          </Box>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
            Configure the function for each §N§ placeholder found in your inputs.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 2, pt: 1.5 }}>
        {/* Placeholder selector chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          {placeholderIds.map((id) => {
            const isSelected = id === selectedId;
            const fn = getFn(id);
            return (
              <Chip
                key={id}
                label={`§${id}§  ·  ${CB_PART_LABELS[fn.type]}`}
                size="small"
                clickable
                onClick={() => setSelectedId(id)}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.72rem',
                  borderColor: isSelected
                    ? theme.palette.primary.main
                    : alpha(theme.palette.divider, 0.7),
                  ...(isSelected && theme.palette.mode === 'dark' && {
                    '& .MuiChip-label': { color: '#0a0e17', fontWeight: 700 },
                  }),
                }}
              />
            );
          })}
        </Box>

        {/* Config panel for the selected placeholder */}
        {selectedId !== null && selectedFn !== null && (
          <Box
            sx={{
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              p: 1.75,
              background: alpha(
                theme.palette.background.default,
                theme.palette.mode === 'dark' ? 0.4 : 0.5,
              ),
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.65rem',
                fontWeight: 700,
                color: theme.palette.primary.main,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 0.5,
              }}
            >
              §{selectedId}§ — Configure Function
            </Typography>
            {renderPartConfig(selectedId, selectedFn)}
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default PayloadFunctionsCard;
